import { useEffect, useState } from 'react';
import { BRAND } from '../constants';

export interface DonutSlice {
    label: string;
    value: number;
    color: string;
}

interface StatusDonutProps {
    slices: DonutSlice[];
    size?: number;
    thickness?: number;
}

// Teal/green status donut built from the live user data (grouped by status).
// Slices animate ("draw in") on mount and whenever the data/filter changes.
export default function StatusDonut({ slices, size = 132, thickness = 18 }: StatusDonutProps) {
    const total = slices.reduce((sum, s) => sum + s.value, 0);
    const radius = (size - thickness) / 2;
    const circumference = 2 * Math.PI * radius;

    // Trigger the grow-in transition: start collapsed, then expand shortly after render.
    const [drawn, setDrawn] = useState(false);
    useEffect(() => {
        setDrawn(false);
        const t = setTimeout(() => setDrawn(true), 80);
        return () => clearTimeout(t);
    }, [slices]);

    // Count the centre total up in step with the draw.
    const [displayTotal, setDisplayTotal] = useState(0);
    useEffect(() => {
        if (total <= 0) { setDisplayTotal(0); return; }
        let raf = 0;
        const start = performance.now();
        const duration = 2400;
        const tick = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplayTotal(Math.round(total * eased));
            if (progress < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [total]);

    let targetOffset = 0;
    return (
        <div style={{ position: 'relative', width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#EEF2F3" strokeWidth={thickness} />
                {total > 0 && slices.map((s, i) => {
                    const targetLen = (s.value / total) * circumference;
                    const len = drawn ? targetLen : 0;
                    const dash = `${len} ${circumference - len}`;
                    const el = (
                        <circle
                            key={i}
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            stroke={s.color}
                            strokeWidth={thickness}
                            strokeDasharray={dash}
                            strokeDashoffset={-targetOffset}
                            strokeLinecap="butt"
                            style={{
                                transition: `stroke-dasharray 1.6s cubic-bezier(0.22, 1, 0.36, 1)`,
                                transitionDelay: `${i * 0.35}s`,
                            }}
                        />
                    );
                    targetOffset += targetLen;
                    return el;
                })}
            </svg>
            <div style={{
                position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', textAlign: 'center',
            }}>
                <span style={{ fontSize: 20, fontWeight: 700, color: BRAND.ink, lineHeight: 1 }}>{displayTotal}</span>
                <span style={{ fontSize: 10, color: '#7c8b91', fontWeight: 500, marginTop: 2 }}>Users</span>
            </div>
        </div>
    );
}
